<script>
// Sidebar do contexto GESTÃO (nível escritório): brand do escritório + bloco de
// topo (Home e AAWZ Hub) + os módulos agrupados em "Partes" + o atalho de
// Configurações fixado no rodapé. A lista de módulos vem do MESMO `buildRoutes` da
// navbar antiga (via buildGestaoGroups) — mesmos módulos, ordem e condições de
// visibilidade; aqui só reagrupamos e renderizamos com o design da nav 2026.
//
// É o único orquestrador das Partes: conhece a rota, deriva a Parte ativa, guarda
// qual está aberta e decide quando rolar. Os componentes filhos são controlados.
import { FEATURES } from '@constants/Text'
import { textOnColor } from '@modules/global/utils/Color'
import { buildGestaoGroups } from '@modules/navegacao-v2/utils/gestao-groups-from-old-nav'
import {
  isActiveRoute,
  routeModuleKey,
} from '@modules/navegacao-v2/utils/visible-modules'
import { deveVerSoFeeFixo } from '@utils/escritorioFeeFixoMigracao'
import {
  filtrarGruposSoFeeFixo,
  filtrarItensSoFeeFixo,
} from '@modules/navegacao-v2/utils/escritorio-fee-fixo-migracao'
import { activeSubitemKey } from '@modules/navegacao-v2/utils/active-subitem'
import {
  bucketModulesIntoPartes,
  PARTE_CONFIGURACOES,
} from '@modules/navegacao-v2/constants/partes'
import { resolveOfficeBrandImage } from '@modules/navegacao-v2/utils/resolve-office-brand-image'
import flyoutHover from '@modules/navegacao-v2/mixins/flyout-hover'
import SidebarShell from './sidebar-shell.vue'
import NavParte from './nav-parte.vue'
import NavItem from './nav-item.vue'
import NavFlyout from './nav-flyout.vue'

// Nenhuma Parte aberta / nenhuma Parte ativa.
const NO_PARTE = ''
// Abaixo deste número de módulos na lista principal o agrupamento cobra mais do
// que entrega: cada Parte viraria um acordeão de um item só, e chegar ao módulo
// passaria a custar um clique que hoje não existe. A sidebar então lista os
// módulos direto, na mesma ordem das Partes. Configurações não entra na conta —
// ela é fixada no rodapé, fora do agrupamento.
export const MIN_MODULOS_PARA_PARTES = 3
// Elemento que rola é o corpo da casca, nunca a janela.
const SCROLL_BODY_SELECTOR = '.sidebar-shell__body'
// Altura do cabeçalho de Parte, usada para decidir se ele cabe inteiro na área
// visível antes de rolar.
const PARTE_HEADER_HEIGHT_PX = 39

export default {
  name: 'GestaoSidebar',
  components: { SidebarShell, NavParte, NavItem, NavFlyout },
  mixins: [flyoutHover],
  props: {
    collapsed: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
  },
  data() {
    return {
      openParteKey: NO_PARTE,
      // Some quando a imagem não carrega (URL quebrada/removida) — cai pro
      // fallback com inicial do escritório em vez de mostrar um ícone quebrado.
      logoLoadFailed: false,
      faviconLoadFailed: false,
    }
  },
  computed: {
    access() {
      return this.$store.getters['parameters/show'] || {}
    },
    activeRouteName() {
      return this.$route ? this.$route.name : ''
    },
    office() {
      const user = this.$currentUser()
      return (user && user.escritorio) || {}
    },
    officeName() {
      return this.office.nome || 'AAWZ'
    },
    officeInitial() {
      return this.officeName.slice(0, 1).toUpperCase()
    },
    // bg-navbar (fundo real da sidebar) é escuro por padrão, INDEPENDENTE da
    // feature "Navegação cor única" (ao contrário do header branco por
    // padrão da navbar antiga) — mesmo critério de _base-logo-loading.vue
    // pra decidir se o texto/logo precisa ser a variante clara.
    bgIsDark() {
      const bgNavbar =
        this.$store.getters['parameters/colors']['bg-navbar'] || '#030309'
      return textOnColor(bgNavbar) === '#FFFFFF'
    },
    // Mesma regra da navbar antiga (nav-bar.vue logoEscritorio/faviconEscritorio),
    // + bgIsDark (ver acima): prioriza a variante branca cadastrada.
    logoEscritorio() {
      return resolveOfficeBrandImage({
        onlyColorFeature: this.access[FEATURES.NAVIGATION_ONLY_COLOR],
        bgIsDark: this.bgIsDark,
        image: this.$store.getters['parameters/logo'],
        imageBranca: this.$store.getters['parameters/logoBranca'],
      })
    },
    faviconEscritorio() {
      return resolveOfficeBrandImage({
        onlyColorFeature: this.access[FEATURES.NAVIGATION_ONLY_COLOR],
        bgIsDark: this.bgIsDark,
        image: this.$store.getters['parameters/favicon'],
        imageBranca: this.$store.getters['parameters/faviconBranca'],
      })
    },
    // Expandida: só a logo (sem nome ao lado), igual à navbar antiga. Some se
    // o escritório não tem logo cadastrada ou se a imagem falhou ao carregar.
    // isCollapsed (e não collapsed): em mobile o drawer é sempre expandido.
    showLogoImage() {
      return !this.isCollapsed && !!this.logoEscritorio && !this.logoLoadFailed
    },
    // Recolhida: só o favicon, mesmo critério de fallback.
    showFaviconImage() {
      return (
        this.isCollapsed && !!this.faviconEscritorio && !this.faviconLoadFailed
      )
    },
    // Cliente final: sidebar só tem 1 módulo (Consolidador) — mostra os
    // subitens sempre visíveis, inline abaixo do módulo, pra clique rápido, em
    // vez do flyout-on-hover (pensado pra sidebar cheia do profissional, com
    // vários módulos disputando espaço).
    isClienteFinal() {
      const user = this.$currentUser()
      return !!(user && user.IS_CLIENTE)
    },
    // Escritório Fee Fixo Migração, não-gestor: usada pelo filtro de `built`
    // abaixo e por `showPartes` (mantém a sidebar plana, sem agrupar em
    // Partes, mesmo com módulos suficientes pra isso).
    soFeeFixo() {
      return deveVerSoFeeFixo(this.$currentUser())
    },
    built() {
      const built = buildGestaoGroups({
        access: this.access,
        currentUser: this.$currentUser(),
        hideCrossSell: this.$hideCrossSell,
        showWorkspace: this.$showWorkspace,
      })
      if (!this.soFeeFixo) return built
      return {
        // Home volta a aparecer (12/08/2026, pedido do usuário — reverte a
        // decisão anterior de escondê-la): só os módulos de `groups`/
        // `topItems` são filtrados pelo allowlist, Home é campo à parte.
        home: built.home,
        groups: filtrarGruposSoFeeFixo(built.groups),
        topItems: filtrarItensSoFeeFixo(built.topItems),
      }
    },
    // Modo recolhido EFETIVO: em mobile a sidebar é um drawer largo e vale sempre
    // o comportamento expandido. Sem isto, `collapsed` herdado do desktop deixaria
    // os módulos dentro de Partes fechadas com o flyout já desligado por mobile —
    // inalcançáveis, e sem botão para reverter (o de recolher é v-if="!mobile").
    isCollapsed() {
      return this.collapsed && !this.mobile
    },
    homeItem() {
      return this.built.home
    },
    // O `|| []` é contrato: um built legado sem a chave não pode quebrar o v-for.
    topItems() {
      return this.built.topItems || []
    },
    groups() {
      return this.built.groups
    },
    allPartes() {
      return bucketModulesIntoPartes(this.groups)
    },
    // Configurações sai da lista principal PELA CHAVE, nunca pela posição.
    mainPartes() {
      return this.allPartes.filter((p) => p.key !== PARTE_CONFIGURACOES)
    },
    configItems() {
      const parte = this.allPartes.find((p) => p.key === PARTE_CONFIGURACOES)
      return parte ? parte.items : []
    },
    // Módulos que a lista principal renderiza, já na ordem fixa das Partes.
    mainModules() {
      return this.mainPartes.reduce((todos, parte) => {
        return todos.concat(parte.items)
      }, [])
    },
    // O cliente final NUNCA entra no agrupamento por Partes. As Partes são um
    // recorte da intenção do GESTOR (ver constants/partes.js); a sidebar dele é
    // uma lista curta de destinos com títulos escritos para ele. Passar por lá
    // renomearia os grupos para o vocabulário do gestor ("Operação" e afins).
    //
    // O corte por `MIN_MODULOS_PARA_PARTES` já barraria o cliente hoje (ele tem
    // dois módulos: Consolidador e Financial Planning), mas depender disso
    // amarraria a sidebar dele à contagem de módulos — o terceiro módulo que
    // um dia aparecer o jogaria nas Partes sem ninguém pedir. O gate por perfil
    // é explícito e não depende de quantos módulos existem.
    //
    // Mesma lógica pro escritório Fee Fixo Migração, não-gestor (achado do
    // usuário, 12/08/2026): liberar CRM/Financial Planning/AAWZ Intelligence
    // além do Fee Fixo levou `mainModules` a 4, cruzando o `MIN_MODULOS_
    // PARA_PARTES` e reagrupando a lista sob títulos "Gestão"/"Operação" —
    // que o usuário quer SEMPRE de fora, não só quando a contagem é baixa.
    showPartes() {
      if (this.isClienteFinal) return false
      if (this.soFeeFixo) return false
      return this.mainModules.length >= MIN_MODULOS_PARA_PARTES
    },
    activeModuleKey() {
      return routeModuleKey(this.activeRouteName)
    },
    // Chaves dos itens que vivem fora das Partes. Derivado, e não uma constante
    // 'solicitacoes': continua correto se um segundo item de topo aparecer.
    standaloneModuleKeys() {
      const itens = this.homeItem
        ? [this.homeItem].concat(this.topItems)
        : this.topItems
      return new Set(itens.map((item) => routeModuleKey(item.routeName)))
    },
    // Deriva dos itens RENDERIZADOS, e não do mapa Módulo→Parte: um escritório sem
    // o módulo contratado que acesse a rota pela URL não abre uma Parte vazia.
    activeParteKey() {
      // Sem agrupamento não existe Parte ativa: derivar uma faria o
      // applyRouteParte abrir e rolar até um cabeçalho que não está no DOM.
      if (!this.showPartes) return NO_PARTE
      if (!this.activeModuleKey) return NO_PARTE
      if (this.standaloneModuleKeys.has(this.activeModuleKey)) return NO_PARTE
      const parte = this.mainPartes.find((p) => this.parteHasActiveItem(p))
      return parte ? parte.key : NO_PARTE
    },
    homeRow() {
      if (!this.homeItem) return null
      return {
        item: this.homeItem,
        active: this.isActive(this.homeItem.routeName),
      }
    },
    topItemRows() {
      return this.topItems.map((item) => this.toRow(item))
    },
    configItemRows() {
      return this.configItems.map((item) => this.toRow(item))
    },
    // Linhas já anotadas: o template não chama função nenhuma, e o número de
    // chamadas por render deixa de crescer com o número de módulos.
    // Linhas da lista plana (sem agrupamento). Mesma anotação de `active` das
    // linhas dentro das Partes — só sem o cabeçalho por cima — mais os subitens
    // inline do cliente final (ver toMainRow).
    //
    // Seções da lista plana. O profissional tem UMA, sem título — a saída
    // renderizada é a mesma de antes. O cliente final tem as dele, na ordem e
    // com os títulos que `buildClienteRoutes` já escreveu — hoje ambos sem
    // título visível (Consolidador e Financial Planning).
    mainSectionRows() {
      if (!this.isClienteFinal) {
        return [
          {
            key: 'modulos',
            title: '',
            rows: this.mainModules.map((item) => this.toMainRow(item)),
          },
        ]
      }
      return this.groups
        .filter((group) => group && (group.items || []).length)
        .map((group, indice) => ({
          key: `secao-${indice}`,
          // `adaptGestaoGroups` já resolve `hiddenTitle` para string vazia.
          title: group.title || '',
          rows: group.items.map((item) => this.toMainRow(item)),
        }))
    },
    parteRows() {
      return this.mainPartes.map((parte) => ({
        key: parte.key,
        label: parte.label,
        icon: parte.icon,
        open: parte.key === this.openParteKey,
        active: parte.key === this.activeParteKey,
        subitems: parte.items,
        items: parte.items.map((item) => this.toRow(item)),
      }))
    },
  },
  watch: {
    activeRouteName() {
      this.applyRouteParte({ scroll: true })
    },
    collapsed(isCollapsed) {
      if (isCollapsed) {
        this.openParteKey = NO_PARTE
        return
      }
      this.applyRouteParte({ scroll: true })
    },
    // `access` vem da store: uma troca de escritório pode cruzar o limite de
    // módulos com a sidebar montada. Sem isto, quem sai da lista plana volta ao
    // agrupamento com a Parte da rota fechada, e quem entra fica com uma Parte
    // aberta guardada que já não existe.
    showPartes() {
      this.applyRouteParte({ scroll: true })
    },
    // URL de logo/favicon mudou (troca de escritório, upload novo) — dá
    // outra chance à imagem antes de assumir que ela vai falhar de novo.
    logoEscritorio() {
      this.logoLoadFailed = false
    },
    faviconEscritorio() {
      this.faviconLoadFailed = false
    },
  },
  // Em created() a região da Parte da rota nasce aberta no primeiro render: em
  // mounted() ela iria de fechada para aberta depois da primeira pintura e a
  // transição de altura animaria uma abertura que o usuário não pediu.
  created() {
    this.applyRouteParte()
  },
  mounted() {
    this.scrollOpenParteIntoView()
  },
  methods: {
    isActive(routeName) {
      return isActiveRoute(routeName, this.activeRouteName)
    },
    toRow(item) {
      return { item, active: this.isActive(item.routeName) }
    },
    // Linha da lista plana (sem agrupamento). Cliente final: os subitens saem
    // do flyout e viram itens inline sempre visíveis (clique rápido) — o pai
    // perde os subitems (desliga o chevron/flyout do NavItem) e NUNCA acende,
    // porque isActive() casa pela chave do módulo e marcaria o pai junto do
    // filho. Só o subitem EXATO acende, pelo mesmo critério do NavFlyout
    // (activeSubitemKey). Inline some no modo recolhido.
    //
    // O item SEM subitens (o Financial Planning do cliente) acende por
    // `isActive`, ou seja, pela chave do módulo: é UM item para as três telas
    // do módulo, então abrir `fp.transacoes` precisa acender a mesma linha que
    // `fp.home` acende. Casar pelo nome exato deixaria a sidebar apagada em
    // duas das três telas.
    toMainRow(item) {
      const inline =
        this.isClienteFinal && item.subitems && item.subitems.length
      if (!inline) {
        return {
          item,
          active: this.isActive(item.routeName),
          subitems: item.subitems,
          inlineSubitems: [],
        }
      }
      return {
        item,
        active: false,
        subitems: [],
        inlineSubitems: this.isCollapsed ? [] : this.toInlineSubitemRows(item),
      }
    },
    toInlineSubitemRows(item) {
      const activeKey = activeSubitemKey(item.subitems, this.activeRouteName)
      return item.subitems.map((sub) => ({
        item: sub,
        active: sub.key === activeKey,
      }))
    },
    parteHasActiveItem(parte) {
      return parte.items.some(
        (item) => routeModuleKey(item.routeName) === this.activeModuleKey
      )
    },
    // Acordeão exclusivo: o estado é uma string única, então duas Partes abertas
    // são impossíveis por construção. Não navega, não rola e não guarda nada.
    toggleParte(key) {
      this.openParteKey = this.openParteKey === key ? NO_PARTE : key
    },
    // A rolagem é guardada por `changed`: navegar entre dois módulos da MESMA
    // Parte não abre nada e portanto não move a coluna sob os olhos de quem
    // acabou de clicar num módulo.
    applyRouteParte(options) {
      const next = this.isCollapsed ? NO_PARTE : this.activeParteKey
      const changed = next !== this.openParteKey
      this.openParteKey = next
      if (!changed || !next) return
      if (options && options.scroll) this.scrollOpenParteIntoView()
    },
    scrollOpenParteIntoView() {
      this.$nextTick(() => {
        const el = this.openParteElement()
        const body = this.$el
          ? this.$el.querySelector(SCROLL_BODY_SELECTOR)
          : null
        if (!el || !body) return
        this.scrollBodyToHeader(body, el)
      })
    },
    openParteElement() {
      const index = this.parteRows.findIndex((p) => p.key === this.openParteKey)
      const refs = this.$refs.partes
      if (index < 0 || !refs || !refs[index]) return null
      return refs[index].$el
    },
    // Sem animação e idempotente: cabeçalho já inteiro na área visível não provoca
    // movimento algum. scrollIntoView({ block: 'nearest' }) não serve — para uma
    // Parte mais alta que a área visível ele conclui que já intersecta e não rola.
    scrollBodyToHeader(body, el) {
      const bodyRect = body.getBoundingClientRect()
      const delta = el.getBoundingClientRect().top - bodyRect.top
      const fits =
        delta >= 0 && delta + PARTE_HEADER_HEIGHT_PX <= bodyRect.height
      if (fits) return
      body.scrollTop += delta
    },
    // Clique num subitem do flyout: fecha o painel e delega a navegação ao pai
    // (mesmo canal `select` dos módulos; resolveDynamicRoute usa o routeName).
    onSelectSubitem(subitem) {
      this.closeFlyoutNow()
      this.$emit('select', subitem)
    },
    onLogoError() {
      this.logoLoadFailed = true
    },
    onFaviconError() {
      this.faviconLoadFailed = true
    },
  },
}
</script>

<template>
  <SidebarShell
    :collapsed="isCollapsed"
    :mobile="mobile"
    :open="open"
    @toggle="$emit('toggle')"
  >
    <template v-slot:brand>
      <div
        class="gestao-brand"
        :class="{
          'gestao-brand--collapsed': isCollapsed,
          'gestao-brand--image': showLogoImage || showFaviconImage,
        }"
      >
        <img
          v-if="showLogoImage"
          class="gestao-brand__image gestao-brand__image--logo"
          :src="logoEscritorio"
          :alt="officeName"
          @error="onLogoError"
        />
        <img
          v-else-if="showFaviconImage"
          class="gestao-brand__image gestao-brand__image--favicon"
          :src="faviconEscritorio"
          :alt="officeName"
          @error="onFaviconError"
        />
        <template v-else>
          <div class="gestao-brand__logo">{{ officeInitial }}</div>
          <div v-if="!isCollapsed" class="gestao-brand__info">
            <div class="gestao-brand__name">{{ officeName }}</div>
          </div>
        </template>
      </div>
    </template>

    <div class="gestao-sidebar__root">
      <div class="gestao-sidebar__top">
        <NavItem
          v-if="homeRow"
          :item="homeRow.item"
          :collapsed="isCollapsed"
          :active="homeRow.active"
          @click="$emit('select', homeRow.item)"
        />
        <NavItem
          v-for="row in topItemRows"
          :key="row.item.key"
          :item="row.item"
          :collapsed="isCollapsed"
          :active="row.active"
          @click="$emit('select', row.item)"
        />
      </div>

      <div class="gestao-sidebar__partes">
        <template v-if="showPartes">
          <NavParte
            v-for="row in parteRows"
            :key="row.key"
            ref="partes"
            :parte="row"
            :open="row.open"
            :active="row.active"
            :collapsed="isCollapsed"
            :mobile="mobile"
            :subitems="row.subitems"
            @toggle="toggleParte"
            @hover-subitems="openFlyout"
            @leave-subitems="scheduleClose"
          >
            <NavItem
              v-for="cell in row.items"
              :key="cell.item.key"
              :item="cell.item"
              :subitems="cell.item.subitems"
              :collapsed="isCollapsed"
              :mobile="mobile"
              :active="cell.active"
              @click="$emit('select', cell.item)"
              @hover-subitems="openFlyout"
              @leave-subitems="scheduleClose"
            />
          </NavParte>
        </template>

        <template v-else>
          <div
            v-for="secao in mainSectionRows"
            :key="secao.key"
            class="gestao-sidebar__secao"
          >
            <!-- Só o cliente final tem seção com título; para o profissional a
                 lista plana é uma seção única sem rótulo, como sempre foi. -->
            <div
              v-if="secao.title && !isCollapsed"
              class="gestao-sidebar__secao-titulo"
            >
              {{ secao.title }}
            </div>
            <template v-for="row in secao.rows">
              <NavItem
                :key="row.item.key"
                :item="row.item"
                :subitems="row.subitems"
                :collapsed="isCollapsed"
                :mobile="mobile"
                :active="row.active"
                @click="$emit('select', row.item)"
                @hover-subitems="openFlyout"
                @leave-subitems="scheduleClose"
              />
              <div
                v-if="row.inlineSubitems.length"
                :key="row.item.key + '-inline'"
                class="gestao-sidebar__inline-subitems"
              >
                <NavItem
                  v-for="cell in row.inlineSubitems"
                  :key="cell.item.key"
                  :item="cell.item"
                  :collapsed="isCollapsed"
                  :active="cell.active"
                  @click="$emit('select', cell.item)"
                />
              </div>
            </template>
          </div>
        </template>
      </div>

      <div v-if="configItemRows.length" class="gestao-sidebar__config">
        <NavItem
          v-for="row in configItemRows"
          :key="row.item.key"
          :item="row.item"
          :subitems="row.item.subitems"
          :collapsed="isCollapsed"
          :mobile="mobile"
          :active="row.active"
          @click="$emit('select', row.item)"
          @hover-subitems="openFlyout"
          @leave-subitems="scheduleClose"
        />
      </div>
    </div>

    <transition name="flyout">
      <NavFlyout
        v-if="flyout"
        :title="flyout.item.label"
        :items="flyout.item.subitems"
        :anchor-top="flyout.top"
        :anchor-left="flyout.left"
        :active-route-name="activeRouteName"
        :match-by="flyout.matchBy"
        @select="onSelectSubitem"
        @mouseenter="cancelClose"
        @mouseleave="scheduleClose"
      />
    </transition>
  </SidebarShell>
</template>

<style lang="sass" scoped>
.gestao-brand
  display: flex
  align-items: center
  gap: 12px
  padding: 20px 18px 16px
  border-bottom: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.08)

  &--collapsed
    justify-content: center
    padding: 20px 0 16px

  // Logo/favicon reais: imagem sozinha (sem nome ao lado) fica centralizada
  // na linha, diferente do fallback (quadrado + nome), que fica alinhado à
  // esquerda como uma label normal.
  &--image
    justify-content: center

  &__logo
    flex-shrink: 0
    width: 38px
    height: 38px
    border-radius: 11px
    background: var(--primary)
    color: var(--text-on-primary, #fff)
    font-weight: 800
    font-size: 15px
    display: flex
    align-items: center
    justify-content: center

  &__image
    max-width: 100%
    object-fit: contain

    &--logo
      max-height: 38px

    &--favicon
      width: 38px
      height: 38px

  &__info
    min-width: 0
    flex: 1

  &__name
    font-weight: 700
    font-size: 15px
    color: var(--nav-fg, #fff)
    white-space: nowrap
    overflow: hidden
    text-overflow: ellipsis

// O rodapé de Configurações vive no slot default, e não no slot `footer` da casca
// (que some no modo recolhido) — daí a coluna ocupar toda a altura para empurrá-lo
// para baixo com margin-top: auto.
.gestao-sidebar__root
  display: flex
  flex-direction: column
  min-height: 100%

.gestao-sidebar__top
  padding-top: 10px

// Subitens do cliente final — sempre visíveis, reusando o próprio NavItem
// (mesmo estilo de pill/ativo dos módulos, igual ao nav-subnav__routes da
// PR #1644). Só o espaçamento é nosso.
.gestao-sidebar__inline-subitems
  display: flex
  flex-direction: column
  gap: 2px
  padding: 0 12px 6px

// Título de seção da lista plana. Nenhum grupo do cliente usa um hoje, mas a
// estrutura os suporta (`mainSectionRows` devolve `title` por seção). Eyebrow
// em caixa alta do design-system: 12px, letter-spacing 0.08em. A cor sai do
// mesmo token de primeiro plano da nav que o resto da casca usa, para
// acompanhar a paleta do escritório.
.gestao-sidebar__secao-titulo
  padding: 12px 18px 6px
  font-size: 12px
  font-weight: 700
  letter-spacing: 0.08em
  text-transform: uppercase
  color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.55)

.gestao-sidebar__config
  margin-top: auto
  padding-top: 8px
  border-top: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.08)
</style>
